/**
 * Sharing a referral link (2026-10-27). On a phone it opens the share
 * sheet (Messages, WhatsApp and so on) with the message already written,
 * because most of these go out as a text to a friend. On a computer, or if
 * the share sheet is closed or blocked, it copies the link instead.
 */
import { supabase } from './supabase';
import { phReferralShared } from './posthog';

export const REFERRAL_BASE = 'https://mediloop.ai/refer/';

export const referralLink = (code: string) => `${REFERRAL_BASE}${code}`;

export const REFERRAL_MESSAGE =
  "mediloop checks medical bills for mistakes and gets them lowered. Here's $40 off if you've got one:";

/** The account's referral code, or null if there isn't one. */
export async function loadReferralCode(): Promise<string | null> {
  const { data, error } = await supabase.rpc('get_or_create_referral_code');
  if (error || typeof data !== 'string' || !data) return null;
  return data;
}

function onPhone(): boolean {
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

/** Returns what happened so the button can say so. */
export async function shareReferral(code: string, source: string): Promise<'shared' | 'copied' | 'failed'> {
  const url = referralLink(code);
  if (onPhone() && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: '$40 off your medical bill', text: REFERRAL_MESSAGE, url });
      phReferralShared({ method: `share_sheet:${source}`, code });
      return 'shared';
    } catch (err) {
      // They closed the sheet. Nothing to do.
      if ((err as Error)?.name === 'AbortError') return 'failed';
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    phReferralShared({ method: `copy_link:${source}`, code });
    return 'copied';
  } catch {
    return 'failed';
  }
}

/* ── Coming in on a friend's link ──────────────────────────────────────
   The /refer page on the site can send someone to /start?rc=<code> to
   get a free first look first (2026-10-27). We keep the code on this
   browser so that when they pay later in the portal, the friend still
   gets credit and they still get the $40 off. */
const REFERRED_BY_KEY = 'ml_referred_by';
const REFERRED_BY_DAYS = 60;

export function rememberReferralFromUrl(): void {
  try {
    const rc = new URLSearchParams(window.location.search).get('rc');
    if (!rc || !/^[A-Za-z0-9-]{4,24}$/.test(rc)) return;
    localStorage.setItem(REFERRED_BY_KEY, JSON.stringify({ code: rc.toLowerCase(), at: Date.now() }));
  } catch {
    /* private mode, fine */
  }
}

export function rememberedReferral(): string | null {
  try {
    const raw = localStorage.getItem(REFERRED_BY_KEY);
    if (!raw) return null;
    const { code, at } = JSON.parse(raw) as { code?: string; at?: number };
    if (!code || !at || Date.now() - at > REFERRED_BY_DAYS * 86_400_000) return null;
    return code;
  } catch {
    return null;
  }
}
